"use client";

import { useRef, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileBalance } from "@getpara/react-sdk";
import { useBalanceSyncStore } from "@/lib/balance-sync-store";
import { evmFetchSingleton, refreshEvmBalances, refreshSolanaBalances, ROBINHOOD_BALANCES_QUERY_KEY } from "./use-token-balances";
import { isNativeToken } from "@/constants/send-modal-helpers";
import { TokenBalance } from "@/constants/types";

const SMART_SYNC_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_SMART_BALANCE_REFRESH !== "false";

// Delays for stability read attempts: 3s, 10s, 20s
// First attempt at 3s gives Para a head start after the initial refresh kick
const SYNC_DELAYS = [3000, 10000, 20000];
const FINAL_TIMEOUT = 25000;

// Types for pre-swap snapshot stability check
interface SwapTokenInfo {
  address: string;
  symbol: string;
  chainId: number;
  isNative: boolean;
}

interface SwapInfo {
  fromToken: SwapTokenInfo;
  toToken: SwapTokenInfo;
}

interface PreSwapSnapshot {
  fromToken: SwapTokenInfo;
  toToken: SwapTokenInfo;
  fromTokenRawQty: string;    // Raw quantity as string, untouched from Para
  toTokenRawQty: string;
  nativeGasRawQty: string;    // "" if fromToken is native (skip this check)
}

/**
 * Find raw quantity string for a token in Para's profileBalance response.
 * Returns "" if token not found (zero balance or not in response).
 */
function findAssetRawQuantity(
  profileBalance: any,
  chainId: number,
  tokenAddress: string,
  isNative: boolean
): string {
  if (!profileBalance?.wallets) return "";

  for (const wallet of profileBalance.wallets) {
    if (wallet.type !== "EVM") continue;

    for (const network of wallet.networks || []) {
      const networkChainId = parseInt(network?.metadata?.evmChainId, 10);
      if (networkChainId !== chainId) continue;

      for (const asset of network.assets || []) {
        const assetSymbol = asset?.metadata?.symbol || "";
        const assetIsNative = isNativeToken(assetSymbol);
        const addressMatch =
          !isNative &&
          (asset.contractAddress || "").toLowerCase() ===
            tokenAddress.toLowerCase() &&
          tokenAddress.length > 0;
        const nativeMatch = isNative && assetIsNative;

        if (addressMatch || nativeMatch) {
          return String(asset.quantity ?? "");
        }
      }
    }
  }
  return ""; // Token not found in Para response = treat as "no balance reported"
}

/**
 * Compute a stability hash from profileBalance data.
 * Returns a sorted JSON string of [contractAddress, valueUSD] pairs
 * across ALL wallets (EVM + Solana).
 * Used as fallback when no pre-swap snapshot is available.
 */
function computeStabilityHash(profileBalance: any): string {
  if (!profileBalance?.wallets) return "";

  const entries: [string, number][] = [];

  for (const wallet of profileBalance.wallets) {
    for (const network of wallet.networks || []) {
      for (const asset of network.assets || []) {
        const address =
          asset.contractAddress || asset.metadata?.symbol || "native";
        const value = asset.value?.value ?? 0;
        entries.push([address, value]);
      }
    }
  }

  // Sort by address for consistent comparison
  entries.sort((a, b) => a[0].localeCompare(b[0]));

  return JSON.stringify(entries);
}

interface UsePostSwapSyncOptions {
  fromChainId?: number;
  toChainId?: number;
  isExternalSolana?: boolean;
  evmAddress?: string | null;
}

export function usePostSwapSync(options: UsePostSwapSyncOptions = {}) {
  const queryClient = useQueryClient();
  const { refetch: refreshBalance, data: profileBalance } = useProfileBalance();
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const isMountedRef = useRef(true);
  const syncIdRef = useRef(0);

  // Ref to avoid closure capturing stale profileBalance in setTimeout callbacks
  const profileBalanceRef = useRef(profileBalance);
  useEffect(() => {
    profileBalanceRef.current = profileBalance;
  }, [profileBalance]);

  // Pre-swap snapshot for quantity-based stability check
  const snapshotRef = useRef<PreSwapSnapshot | null>(null);

  // Track previous attempt's observations for two-in-a-row stability requirement
  const previousAttemptRef = useRef<{
    fromQty: string;
    toQty: string;
    nativeQty: string;
  } | null>(null);

  // Robinhood Chain (4663) pre-swap snapshot for change detection
  // Stores the full token list so we can detect ANY balance change (handles partial fills)
  const robinhoodSnapshotRef = useRef<TokenBalance[] | null>(null);

  const { startSync, recordHash, endSync, hashesMatch } = useBalanceSyncStore();

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear all timers on unmount
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  /**
   * Capture pre-swap snapshot of token quantities.
   * Call this BEFORE executeRoute starts.
   */
  const captureSnapshot = useCallback((info: SwapInfo) => {
    const pb = profileBalanceRef.current;
    if (!pb) {
      snapshotRef.current = null;
      previousAttemptRef.current = null;
      return;
    }

    const fromQty = findAssetRawQuantity(
      pb,
      info.fromToken.chainId,
      info.fromToken.address,
      info.fromToken.isNative
    );
    const toQty = findAssetRawQuantity(
      pb,
      info.toToken.chainId,
      info.toToken.address,
      info.toToken.isNative
    );
    const nativeQty = info.fromToken.isNative
      ? "" // skip — fromToken IS native
      : findAssetRawQuantity(pb, info.fromToken.chainId, "", true);

    snapshotRef.current = {
      fromToken: info.fromToken,
      toToken: info.toToken,
      fromTokenRawQty: fromQty,
      toTokenRawQty: toQty,
      nativeGasRawQty: nativeQty,
    };

    // Robinhood Chain (4663): capture current balances from React Query cache for change detection
    const isRobinhoodSwap = info.fromToken.chainId === 4663 || info.toToken.chainId === 4663;
    if (isRobinhoodSwap && options.evmAddress) {
      const cachedRhData = queryClient.getQueryData<TokenBalance[] | null>([
        ROBINHOOD_BALANCES_QUERY_KEY,
        options.evmAddress.toLowerCase(),
      ]);
      robinhoodSnapshotRef.current = cachedRhData ? [...cachedRhData] : null;
    } else {
      robinhoodSnapshotRef.current = null;
    }
  }, [options.evmAddress, queryClient]);

  /**
   * Kick off a balance refresh (fire and forget).
   * Uses invalidateQueries (marks stale, keeps old data visible) instead of
   * resetQueries (which would blank out the ref and race against our read).
   */
  const executeRefresh = useCallback(async () => {
    // Clear EVM fetch singleton (this is our own cache, safe to clear)
    evmFetchSingleton.cachedResult = null;
    evmFetchSingleton.lastFetchKey = "";
    evmFetchSingleton.inFlight = false;
    refreshEvmBalances();

    // Invalidate Solana tokens cache - all components share this cache via React Query
    // This triggers ONE refetch, all subscribed components re-render with fresh data
    refreshSolanaBalances(queryClient);

    // Use invalidateQueries (marks stale, keeps old data visible until new data arrives)
    // NOT resetQueries (which would blank out the ref and race against our read)
    await queryClient.invalidateQueries({ queryKey: ["useProfileBalance"] });
    await queryClient.invalidateQueries({ queryKey: ["balance"] });
    await queryClient.invalidateQueries({ queryKey: ["profileBalance"] });
    await queryClient.invalidateQueries({ queryKey: ["tokenBalances"] });
    // Also invalidate swap form's direct Solana balance query to keep form in sync with portfolio
    await queryClient.invalidateQueries({ queryKey: ["solanaBalance"] });

    // Kick off a fresh fetch — don't await, let it resolve naturally
    // The useEffect will pick up new data and update profileBalanceRef
    try {
      refreshBalance();
    } catch (e) {
      console.warn("Balance refresh failed:", e);
    }
  }, [queryClient, refreshBalance]);

  /**
   * Legacy refresh behavior (setTimeout 3s + 8s for BSC)
   */
  const executeLegacyRefresh = useCallback(
    (fromChainId?: number, toChainId?: number) => {
      const refreshBalances = async () => {
        evmFetchSingleton.cachedResult = null;
        evmFetchSingleton.lastFetchKey = "";
        evmFetchSingleton.inFlight = false;
        refreshEvmBalances();

        // Invalidate Solana tokens cache via React Query
        refreshSolanaBalances(queryClient);

        queryClient.resetQueries({ queryKey: ["useProfileBalance"] });
        queryClient.resetQueries({ queryKey: ["balance"] });

        await queryClient.invalidateQueries({ queryKey: ["profileBalance"] });
        await queryClient.invalidateQueries({ queryKey: ["tokenBalances"] });
        // Also invalidate swap form's direct Solana balance query
        await queryClient.invalidateQueries({ queryKey: ["solanaBalance"] });

        try {
          await refreshBalance();
        } catch (e) {
          console.warn("Balance refresh failed:", e);
        }
      };

      // 3s refresh for all chains
      const timer1 = setTimeout(refreshBalances, 3000);
      timersRef.current.push(timer1);

      // Additional 8s refresh for BSC (chain 56)
      const isBscSwap = fromChainId === 56 || toChainId === 56;
      if (isBscSwap) {
        const timer2 = setTimeout(refreshBalances, 8000);
        timersRef.current.push(timer2);
      }
    },
    [queryClient, refreshBalance]
  );

  // KNOWN ISSUE (2026-05-24): Phantom-specific post-swap sync may
  // require manual refresh after sells on some user setups.
  // Solflare and Para work correctly with the current fast-path logic.
  // Diagnostic logs showed inconsistent behavior across browser sessions,
  // suggesting Phantom extension state (cached site permissions, connected
  // wallet caching) plays a role. To investigate fresh:
  // 1. Test in fully clean browser profile with fresh Phantom install
  // 2. Compare PublicKey object identity (=== vs .equals()) for Phantom
  // 3. Check if Phantom's signAndSendTransaction resolves before chain confirmation

  /**
   * Main sync trigger function
   */
  const triggerPostSwapSync = useCallback(() => {
    // Cancel any existing sync cycle and clear stale snapshot
    clearAllTimers();
    // Note: Do NOT clear snapshotRef here — it was just captured before this call

    // EXTERNAL SOLANA WALLETS: Skip Para stability check entirely
    // Para's profileBalance doesn't have data for Phantom/Solflare wallets,
    // so the stability check would pass immediately with stale/empty data.
    // Instead, do direct React Query invalidation with timed retries.
    if (options.isExternalSolana) {
      // Immediate invalidation
      refreshSolanaBalances(queryClient);

      // Retry at 2s to catch propagation delay
      const timer1 = setTimeout(() => {
        if (!isMountedRef.current) return;
        refreshSolanaBalances(queryClient);
      }, 2000);
      timersRef.current.push(timer1);

      // Final retry at 5s for slow RPC or congested network
      const timer2 = setTimeout(() => {
        if (!isMountedRef.current) return;
        refreshSolanaBalances(queryClient);
      }, 5000);
      timersRef.current.push(timer2);

      return;
    }

    // ROBINHOOD CHAIN (4663): Separate refresh loop with change detection
    // Runs in parallel with Para stability check (Para doesn't know about chain 4663)
    const isRobinhoodSwap = options.fromChainId === 4663 || options.toChainId === 4663;
    if (isRobinhoodSwap && options.evmAddress && robinhoodSnapshotRef.current) {
      const rhAddress = options.evmAddress.toLowerCase();
      const rhSnapshot = robinhoodSnapshotRef.current;

      // Helper to check if balances changed from snapshot (plain inequality)
      const balancesChanged = (current: TokenBalance[] | null): boolean => {
        if (!current || !rhSnapshot) return false;
        if (current.length !== rhSnapshot.length) return true;

        // Build lookup map for quick comparison
        const snapshotMap = new Map<string, string>();
        for (const t of rhSnapshot) {
          snapshotMap.set(t.address.toLowerCase() || `native-${t.symbol}`, t.balance);
        }

        for (const t of current) {
          const key = t.address.toLowerCase() || `native-${t.symbol}`;
          const snapshotBalance = snapshotMap.get(key);
          if (snapshotBalance !== t.balance) return true; // Any change counts
        }
        return false;
      };

      // Retry delays: 3s, 8s, 13s (gives Blockscout indexer time to catch up)
      const RH_RETRY_DELAYS = [3000, 8000, 13000];
      const RH_MAX_ATTEMPTS = 3;
      let rhAttempt = 0;

      const attemptRobinhoodRefresh = async () => {
        if (!isMountedRef.current) return;
        if (!robinhoodSnapshotRef.current) return; // A prior attempt already succeeded or hit max
        rhAttempt++;

        try {
          // Fetch with fresh=1 to bypass server cache
          const res = await fetch(`/api/rh-balances?address=${rhAddress}&fresh=1`);
          if (!res.ok) return;

          const data = await res.json();
          const freshTokens = data.tokens as TokenBalance[] | null;

          // Check if balance changed from pre-swap snapshot
          const changed = balancesChanged(freshTokens);

          if (changed) {
            // Balance changed — update the React Query cache with fresh data
            queryClient.setQueryData(
              [ROBINHOOD_BALANCES_QUERY_KEY, rhAddress],
              freshTokens
            );
            robinhoodSnapshotRef.current = null; // Clear snapshot, we're done
          } else if (rhAttempt >= RH_MAX_ATTEMPTS) {
            // Max attempts reached, accept current data (swap may have failed or indexer very slow)
            queryClient.setQueryData(
              [ROBINHOOD_BALANCES_QUERY_KEY, rhAddress],
              freshTokens
            );
            robinhoodSnapshotRef.current = null;
          }
          // If not changed and attempts remain, the next scheduled timer will retry
        } catch {
          // Silently fail - next scheduled attempt will retry
        }
      };

      // Schedule retry attempts
      RH_RETRY_DELAYS.forEach((delay) => {
        const timer = setTimeout(attemptRobinhoodRefresh, delay);
        timersRef.current.push(timer);
      });
    }

    // If feature flag is off, use legacy behavior
    if (!SMART_SYNC_ENABLED) {
      snapshotRef.current = null;
      previousAttemptRef.current = null;
      executeLegacyRefresh(options.fromChainId, options.toChainId);
      return;
    }

    // Increment sync ID to track this specific sync cycle
    const currentSyncId = ++syncIdRef.current;

    // Reset previous-attempt tracking for fresh sync cycle
    previousAttemptRef.current = null;

    // Start the sync state
    startSync();

    // Kick off refresh ONCE, immediately
    // Data arrives via useEffect → profileBalanceRef update
    executeRefresh();

    // Safety net: second refresh at 10s in case first silently failed
    const secondRefreshTimer = setTimeout(() => {
      if (!isMountedRef.current || syncIdRef.current !== currentSyncId) return;
      executeRefresh();
    }, 10000);
    timersRef.current.push(secondRefreshTimer);

    // Stability attempts — just READ the ref, no cache manipulation
    SYNC_DELAYS.forEach((delay, attemptIndex) => {
      const timer = setTimeout(() => {
        // Abort if component unmounted or a new sync started
        if (!isMountedRef.current || syncIdRef.current !== currentSyncId) {
          return;
        }

        // Read current ref state (no refresh here — data arrives naturally via useEffect)
        const pb = profileBalanceRef.current;

        // Check stability using appropriate method
        if (snapshotRef.current) {
          try {
            // NEW: Quantity-based stability check
            const snap = snapshotRef.current;

            const curFromQty = findAssetRawQuantity(
              pb,
              snap.fromToken.chainId,
              snap.fromToken.address,
              snap.fromToken.isNative
            );
            const curToQty = findAssetRawQuantity(
              pb,
              snap.toToken.chainId,
              snap.toToken.address,
              snap.toToken.isNative
            );
            const curNativeQty = snap.fromToken.isNative
              ? ""
              : findAssetRawQuantity(pb, snap.fromToken.chainId, "", true);

            // Raw "did this value meaningfully change" — true requires non-empty different value
            const toChangedStrict = curToQty !== "" && curToQty !== snap.toTokenRawQty;
            const nativeChangedStrict = snap.fromToken.isNative
              ? true
              : (curNativeQty !== "" && curNativeQty !== snap.nativeGasRawQty);

            // "Para has clearly updated" signal — both to-token AND native show genuine change.
            // When true, we can trust that empty from-token means "token went to zero" not "no data yet".
            const paraHasUpdated = toChangedStrict && (snap.fromToken.isNative ? true : nativeChangedStrict);

            // from-token: empty counts as "gone to zero" ONLY if Para has clearly updated.
            const fromChanged =
              (curFromQty !== "" && curFromQty !== snap.fromTokenRawQty) ||
              (curFromQty === "" && paraHasUpdated);

            const toChanged = toChangedStrict;
            const nativeChanged = nativeChangedStrict;

            const stable = fromChanged && toChanged && nativeChanged;

            if (stable) {
              // Check if previous attempt showed the SAME values (two-in-a-row requirement)
              const prev = previousAttemptRef.current;
              const matchesPrevious = prev !== null
                && prev.fromQty === curFromQty
                && prev.toQty === curToQty
                && prev.nativeQty === curNativeQty;

              if (matchesPrevious) {
                snapshotRef.current = null;
                previousAttemptRef.current = null;
                clearAllTimers();
                endSync();
                return;
              }

              // Stable values but no previous attempt to compare — record and wait for next attempt
              previousAttemptRef.current = {
                fromQty: curFromQty,
                toQty: curToQty,
                nativeQty: curNativeQty,
              };
            } else {
              // Not stable this attempt — clear the previous-attempt cache so we require fresh consecutive matches
              previousAttemptRef.current = null;
            }
          } catch {
            // Stability check failed, will retry on next attempt
          }
        } else {
          // FALLBACK: Hash-based stability check (unchanged)
          const hash = computeStabilityHash(pb);

          // Record the hash
          recordHash(hash);

          // Check if we have stability (two consecutive matching hashes)
          const matched = hashesMatch();
          if (matched) {
            clearAllTimers();
            endSync();
            return;
          }
        }
      }, delay);

      timersRef.current.push(timer);
    });

    // Final timeout - paint whatever we have
    const finalTimer = setTimeout(() => {
      if (!isMountedRef.current || syncIdRef.current !== currentSyncId) {
        return;
      }

      snapshotRef.current = null;
      previousAttemptRef.current = null;
      clearAllTimers();
      endSync();
    }, FINAL_TIMEOUT);

    timersRef.current.push(finalTimer);
  }, [
    clearAllTimers,
    endSync,
    executeRefresh,
    executeLegacyRefresh,
    hashesMatch,
    options.evmAddress,
    options.fromChainId,
    options.toChainId,
    queryClient,
    recordHash,
    startSync,
  ]);

  return { triggerPostSwapSync, captureSnapshot };
}
